import { useMemo } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import Iconify from 'src/components/Iconify';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'src/@/components/ui/card';
import { Label } from 'src/@/components/ui/label';
import { RadioGroup, RadioGroupItem } from 'src/@/components/ui/radio-group';

const LABEL_CLS = 'text-xs font-semibold uppercase tracking-wider text-gray-500';
const HINT_CLS = 'text-xs text-gray-400';

const DEFAULT_TOM = 'auto';
const DEFAULT_FIRST_MESSAGE_MODE = 'identify_and_purpose';
const DEFAULT_NOMENCLATURA = 'vendedor';

/** Card selecionável: RadioGroupItem escondido + Label estilizada (peer sr-only). */
function OpcaoCard({ grupo, opcao, ativo }) {
    return (
        <div key={opcao.value}>
            <RadioGroupItem value={opcao.value} id={`${grupo}-${opcao.value}`} className="peer sr-only" />
            <Label
                htmlFor={`${grupo}-${opcao.value}`}
                className={`flex flex-col gap-2 p-4 rounded-xl border cursor-pointer transition-all ${ativo
                    ? 'border-[#7C3AED] bg-violet-50 shadow-sm shadow-[#7C3AED]/20'
                    : 'border-gray-200 bg-white hover:border-violet-200 hover:bg-violet-50/40'
                    }`}
            >
                <div className="flex items-center gap-2">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${ativo ? 'bg-[#7C3AED] text-white' : 'bg-gray-100 text-gray-500'}`}>
                        <Iconify icon={opcao.icon} width={16} />
                    </div>
                    <span className={`text-sm font-semibold ${ativo ? 'text-[#7C3AED]' : 'text-gray-900'}`}>{opcao.title}</span>
                </div>
                <p className="text-xs text-gray-500 leading-relaxed">{opcao.description}</p>
            </Label>
        </div>
    );
}

export default function TomConversaSection({ aiAgent, changeModuleConfig, changeTomConversa }) {
    const { t } = useTranslation('settings');
    const conversa = aiAgent?.config?.module?.ecommerce?.conversa || {};

    const tom = conversa.tom || DEFAULT_TOM;
    const firstMessageMode = conversa.firstMessageMode || DEFAULT_FIRST_MESSAGE_MODE;
    const nomenclatura = conversa.nomenclaturaVendedor || DEFAULT_NOMENCLATURA;

    // `changeTomConversa` e um atalho do AgenteAiDetails que escreve direto em
    // module[moduleKey].conversa.tom. Mantemos o fallback para o caso de o pai
    // nao passar o callback.
    const setTom = (v) => {
        if (typeof changeTomConversa === 'function') { changeTomConversa(v); return; }
        changeModuleConfig({ conversa: { ...conversa, tom: v } });
    };
    const updateConversa = (patch) => changeModuleConfig({ conversa: { ...conversa, ...patch } });

    const tons = useMemo(() => [
        {
            value: 'auto',
            icon: 'mdi:auto-fix',
            title: t('aiAgents.ecommerce.tom.autoTitle', 'Automático'),
            description: t('aiAgents.ecommerce.tom.autoDesc', 'Lê o cliente: se ele é direto, responde direto; se está explorando, oferece mais contexto.'),
        },
        {
            value: 'direto',
            icon: 'mdi:arrow-right-bold-outline',
            title: t('aiAgents.ecommerce.tom.diretoTitle', 'Direto'),
            description: t('aiAgents.ecommerce.tom.diretoDesc', 'Vai ao ponto, sem rodeios. Bom para volume alto e ticket baixo.'),
        },
        {
            value: 'consultivo',
            icon: 'mdi:hand-heart-outline',
            title: t('aiAgents.ecommerce.tom.consultivoTitle', 'Consultivo'),
            description: t('aiAgents.ecommerce.tom.consultivoDesc', 'Entende a necessidade antes de sugerir. Bom para ticket alto e produto técnico.'),
        },
    ], [t]);

    const primeiras = useMemo(() => [
        {
            value: 'generic',
            icon: 'mdi:message-outline',
            title: t('aiAgents.ecommerce.tom.firstGenericTitle', 'Genérica'),
            description: t('aiAgents.ecommerce.tom.firstGenericDesc', '"Oi, tudo bem? Como posso te ajudar?"'),
        },
        {
            value: 'identify_only',
            icon: 'mdi:badge-account-outline',
            title: t('aiAgents.ecommerce.tom.firstIdentifyTitle', 'Com a marca'),
            description: t('aiAgents.ecommerce.tom.firstIdentifyDesc', '"Olá! Sou a assistente da [loja]. Como posso te ajudar?"'),
        },
        {
            value: 'identify_and_purpose',
            icon: 'mdi:account-question-outline',
            title: t('aiAgents.ecommerce.tom.firstPurposeTitle', 'Com a marca e sondando'),
            description: t('aiAgents.ecommerce.tom.firstPurposeDesc', '"...você procura algum produto específico ou quer ver as novidades?"'),
        },
    ], [t]);

    const nomenclaturas = useMemo(() => [
        { value: 'vendedor', label: t('aiAgents.ecommerce.tom.nomVendedor', 'vendedor') },
        { value: 'consultor', label: t('aiAgents.ecommerce.tom.nomConsultor', 'consultor') },
        { value: 'atendente', label: t('aiAgents.ecommerce.tom.nomAtendente', 'atendente') },
        { value: 'setor_responsavel', label: t('aiAgents.ecommerce.tom.nomSetor', 'setor responsável') },
    ], [t]);

    return (
        <Card className="rounded-xl border border-gray-100 bg-white">
            <CardHeader>
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-violet-50">
                        <Iconify icon="mdi:chat-processing-outline" width={20} className="text-violet-600" />
                    </div>
                    <div>
                        <CardTitle>{t('aiAgents.ecommerce.tom.title', 'Tom da Conversa')}</CardTitle>
                        <CardDescription>{t('aiAgents.ecommerce.tom.subtitle', 'Como a assistente fala, como abre a conversa e como chama o seu time.')}</CardDescription>
                    </div>
                </div>
            </CardHeader>
            <CardContent className="flex flex-col">
                <div className="flex flex-col gap-2">
                    <Label className={LABEL_CLS}>{t('aiAgents.ecommerce.tom.tomLabel', 'Tonalidade')}</Label>
                    <RadioGroup value={tom} onValueChange={setTom} className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-2">
                        {tons.map((o) => <OpcaoCard key={o.value} grupo="tom" opcao={o} ativo={tom === o.value} />)}
                    </RadioGroup>
                    <p className={HINT_CLS}>
                        <Trans t={t} i18nKey="aiAgents.ecommerce.tom.tomHint" defaults="Padrão: <code1>auto</code1>." components={{ code1: <code /> }} />
                    </p>
                </div>

                <div className="mt-6 pt-6 border-t border-gray-100 flex flex-col gap-2">
                    <Label className={LABEL_CLS}>{t('aiAgents.ecommerce.tom.firstLabel', 'Comportamento da primeira mensagem')}</Label>
                    <RadioGroup
                        value={firstMessageMode}
                        onValueChange={(v) => updateConversa({ firstMessageMode: v })}
                        className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-2"
                    >
                        {primeiras.map((o) => <OpcaoCard key={o.value} grupo="first" opcao={o} ativo={firstMessageMode === o.value} />)}
                    </RadioGroup>
                </div>

                <div className="mt-6 pt-6 border-t border-gray-100 flex flex-col gap-2">
                    <Label className={LABEL_CLS}>{t('aiAgents.ecommerce.tom.nomenclaturaLabel', 'Como chamar quem recebe o atendimento')}</Label>
                    <RadioGroup
                        value={nomenclatura}
                        onValueChange={(v) => updateConversa({ nomenclaturaVendedor: v })}
                        className="flex flex-wrap gap-3 mt-2"
                    >
                        {nomenclaturas.map((o) => {
                            const ativo = nomenclatura === o.value;
                            return (
                                <div key={o.value}>
                                    <RadioGroupItem value={o.value} id={`nom-${o.value}`} className="peer sr-only" />
                                    <Label
                                        htmlFor={`nom-${o.value}`}
                                        className={`px-4 py-2 rounded-xl border cursor-pointer text-sm transition-all ${ativo
                                            ? 'border-[#7C3AED] bg-violet-50 text-[#7C3AED] font-semibold shadow-sm shadow-[#7C3AED]/20'
                                            : 'border-gray-200 bg-white text-gray-600 hover:border-violet-200 hover:bg-violet-50/40'
                                            }`}
                                    >
                                        {o.label}
                                    </Label>
                                </div>
                            );
                        })}
                    </RadioGroup>
                    <p className={HINT_CLS}>{t('aiAgents.ecommerce.tom.nomenclaturaHint', 'O termo aparece na mensagem de encaminhamento — "vou te passar para um vendedor". Material de construção diz vendedor; ótica diz consultor; SAC diz setor responsável.')}</p>
                </div>
            </CardContent>
        </Card>
    );
}
